import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authApiClient } from "../../api/authApiClient";
import { handleApiError } from "../../../../shared/utils/handleApiError";
import { loginSchema } from "../../schema";
import { useAuthStore } from "../../store";
import type { LoginCredentials } from "../../type";
import { getDefaultRouteForRole } from "../../permissions";
import { AuthInput } from "../ui/AuthInput";
import { PasswordInput } from "../ui/PasswordInput";
import { AuthCheckbox } from "../ui/AuthCheckbox";
import Button from "../../../../shared/components/Button";

type LoginFormData = LoginCredentials;

export const LoginForm: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { login } = useAuthStore();
	const [formData, setFormData] = useState<LoginFormData>({
		email: "",
		password: "",
		staySignedIn: false,
	});
	const [errors, setErrors] = useState<Partial<LoginFormData>>({});
	const [isLoading, setIsLoading] = useState(false);
	const [serverError, setServerError] = useState<string>("");

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev: LoginFormData) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));

		// Clear error messages as soon as user starts typing
		if (errors[name as keyof LoginFormData]) {
			setErrors((prev: Partial<LoginFormData>) => ({
				...prev,
				[name]: undefined,
			}));
		}

		// Also clear any server error when they start typing
		if (serverError) {
			setServerError("");
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setErrors({});
		setServerError("");

		try {
			// Check if the form data is valid before sending to server
			const validatedData = loginSchema.parse(formData);

			// Try to log them in
			const response = await authApiClient.login(validatedData);
			if (response.token && response.user) {
				// Save their login info
				await login(response.user, response.token, formData.staySignedIn);
				// Merchant users who haven't completed onboarding go to reset password first
				// const isMerchant = response.user.roleName === 'MERCHANT_ADMIN' || response.user.roleName === 'MERCHANT_USER';
				// if (isMerchant
				// 	// && !response.user.isProfileCompleted
				// ) {
				// 	navigate('/onboarding/reset-password');
				// 	return;
				// }
				// Navigate to role-appropriate default route
				const redirectTo = searchParams.get('redirect_to');
				navigate(redirectTo ?? getDefaultRouteForRole(response.user.roleName));
			}
		} catch (error: unknown) {
			if (
				error &&
				typeof error === "object" &&
				"errors" in error &&
				Array.isArray((error as { errors: unknown }).errors)
			) {
				// Zod validation errors - safe type casting
				const fieldErrors: Partial<LoginFormData> = {};
				const zodError = error as {
					errors: Array<{ path?: string[]; message: string }>;
				};
				zodError.errors.forEach((err) => {
					if (err.path && err.path[0] && typeof err.message === "string") {
						const field = err.path[0];
						// Only assign to valid form fields
						if (
							field === "email" ||
							field === "password" ||
							field === "staySignedIn"
						) {
							const typedFieldErrors = fieldErrors as Record<string, string>;
							typedFieldErrors[field] = err.message;
						}
					}
				});
				setErrors(fieldErrors);
			} else {
				console.error("Login error:", error);
				// Server error - show user-friendly message
				setServerError(handleApiError(error));
			}
		} finally {
			// Always stop the loading spinner, whether success or failure
			setIsLoading(false);
		}
	};

	return (
		<div className="space-y-8">
			<div className="text-center">
				<h1 className="text-[28px] font-bold text-foreground mb-2">Sign In</h1>
				<p className="text-sm text-muted-foreground">
					Sign in to your Collective Synergy admin account.
				</p>
			</div>

			<form
				onSubmit={handleSubmit}
				className="space-y-6">
				<AuthInput
					name="email"
					type="email"
					placeholder="Email"
					value={formData.email}
					onChange={handleChange}
					error={errors.email}
					required
				/>
				<div className="space-y-1">
					<PasswordInput
						name="password"
						placeholder="Password"
						value={formData.password}
						onChange={handleChange}
						error={errors.password}
						required
					/>
					<div className="items-start flex px-1">
						{serverError && (
							<div className="text-destructive text-sm font-normal text-center">
								{serverError}
							</div>
						)}
					</div>
				</div>

				<div className="flex items-center justify-between">
					<AuthCheckbox
						name="staySignedIn"
						label="Stay signed in"
						checked={formData.staySignedIn}
						onChange={handleChange}
					/>

					<Link
						to="/forgot-password"
						className="text-sm text-foreground font-medium hover:underline transition-colors">
						Forgot your password?
					</Link>
				</div>

				<Button
					type="submit"
					loading={isLoading}
					className="w-full">
					Log In
				</Button>
			</form>
		</div>
	);
};
